import { DrinkActivitySummary } from "../../../services/drinkService";
import { toAbvFraction } from "../../../services/drinks/utils/numbers";
import {
    ALCOHOL_DENSITY,
    DEFAULT_DRINK_DURATION_MINUTES,
    DISTRIBUTION_FACTORS,
    DRINKING_DAY_PADDING_MINUTES,
    ELIMINATION_RATE_PER_HOUR,
} from "./constants";
import { PromillePoint, Sex, SoberInfo } from "./types";

export const calculatePureAlcoholGrams = (amountMl: number, storedPromille: number): number =>
    amountMl * toAbvFraction(storedPromille) * ALCOHOL_DENSITY;

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const TIMELINE_PADDING_MS = DRINKING_DAY_PADDING_MINUTES * 60 * 1000;

export const computeVolumeTotals = (
    activities: DrinkActivitySummary[],
    dayStartMs: number,
    dayEndMs: number
) => {
    const weekStartMs = dayStartMs - 6 * MS_IN_DAY;
    const monthStartMs = dayStartMs - 29 * MS_IN_DAY;

    return activities.reduce(
        (totals, activity) => {
            const timestamp = activity.timestamp?.getTime();
            if (typeof timestamp !== "number") {
                return totals;
            }

            if (timestamp >= dayStartMs && timestamp < dayEndMs) {
                totals.day += activity.amountMl;
            }
            if (timestamp >= weekStartMs && timestamp < dayEndMs) {
                totals.week += activity.amountMl;
            }
            if (timestamp >= monthStartMs && timestamp < dayEndMs) {
                totals.month += activity.amountMl;
            }

            return totals;
        },
        { day: 0, week: 0, month: 0 }
    );
};

export const getPromilleAtTime = (points: PromillePoint[], targetMs: number): number => {
    if (points.length === 0 || targetMs < points[0].timestamp) {
        return 0;
    }

    let value = points[0].promille;
    for (const point of points) {
        if (point.timestamp > targetMs) {
            break;
        }
        value = point.promille;
    }

    return value;
};

export const buildFullPromilleTimeline = (
    activities: DrinkActivitySummary[],
    weightKg: number,
    sex: Sex,
    nowMs: number
): PromillePoint[] => {
    let startMs = nowMs;
    let endMs = nowMs;
    let hasTimestamp = false;
    for (const activity of activities) {
        const finish = activity.timestamp?.getTime();
        if (typeof finish !== "number") continue;
        hasTimestamp = true;
        const durationMinutes =
            activity.durationMinutes != null && activity.durationMinutes >= 0
                ? activity.durationMinutes
                : DEFAULT_DRINK_DURATION_MINUTES;
        const activityStart = activity.startedAt?.getTime() ?? finish - durationMinutes * 60 * 1000;
        startMs = Math.min(startMs, Math.min(activityStart, finish) - 60 * 1000);
        endMs = Math.max(endMs, finish);
    }

    if (!hasTimestamp) return [];
    const points = buildDailyPromilleTimeline(activities, startMs, endMs, weightKg, sex, nowMs);
    return endMs === nowMs ? points.filter((point) => point.timestamp <= nowMs) : points;
};

export const buildDailyPromilleTimeline = (
    activities: DrinkActivitySummary[],
    startMs: number,
    endMs: number,
    weightKg: number,
    sex: Sex,
    nowMs: number
): PromillePoint[] => {
    const distributionFactor = DISTRIBUTION_FACTORS[sex];
    const eliminationRate = ELIMINATION_RATE_PER_HOUR[sex] ?? 0.15;
    if (!weightKg || weightKg <= 0 || !distributionFactor || eliminationRate <= 0) return [];
    const eliminationPerMs = eliminationRate / (60 * 60 * 1000);
    const events = buildActivityEvents(activities, startMs, endMs, weightKg, distributionFactor);
    return buildTimelinePoints(events, startMs, endMs, nowMs, eliminationPerMs);
};

type TimelineEvents = { rate: Map<number, number>; instant: Map<number, number> };

const buildActivityEvents = (
    activities: DrinkActivitySummary[],
    startMs: number,
    endMs: number,
    weightKg: number,
    distributionFactor: number
): TimelineEvents => {
    const rate = new Map<number, number>();
    const instant = new Map<number, number>();
    const add = (events: Map<number, number>, timestamp: number, amount: number) => {
        events.set(timestamp, (events.get(timestamp) ?? 0) + amount);
    };

    for (const activity of activities) {
        const finish = activity.timestamp?.getTime();
        if (typeof finish !== "number") continue;
        const durationMinutes =
            activity.durationMinutes != null && activity.durationMinutes >= 0
                ? activity.durationMinutes
                : DEFAULT_DRINK_DURATION_MINUTES;
        const durationMs = durationMinutes * 60 * 1000;
        const candidateStart = activity.startedAt?.getTime() ??
            (durationMs > 0 ? finish - durationMs : finish);
        const startTimestamp = Math.max(startMs, candidateStart);
        const endTimestamp = Math.max(startMs, finish);
        if (endTimestamp < startMs || startTimestamp > endMs) continue;
        const contribution = calculatePureAlcoholGrams(activity.amountMl, activity.promille) /
            (weightKg * distributionFactor);
        if (durationMs <= 0) {
            add(instant, endTimestamp, contribution);
            continue;
        }
        const effectiveEnd = Math.min(endTimestamp, endMs);
        const effectiveStart = Math.max(startTimestamp, startMs);
        if (effectiveEnd <= effectiveStart) {
            add(instant, endTimestamp, contribution);
            continue;
        }
        const eventRate = contribution / (effectiveEnd - effectiveStart);
        add(rate, effectiveStart, eventRate);
        add(rate, effectiveEnd, -eventRate);
    }

    return { rate, instant };
};

const buildTimelinePoints = (
    events: TimelineEvents,
    startMs: number,
    endMs: number,
    nowMs: number,
    eliminationPerMs: number
): PromillePoint[] => {
    const changePoints = new Set<number>([startMs, endMs]);
    events.rate.forEach((_delta, timestamp) => changePoints.add(timestamp));
    events.instant.forEach((_delta, timestamp) => changePoints.add(timestamp));
    if (nowMs >= startMs && nowMs <= endMs) changePoints.add(nowMs);
    const sortedPoints = Array.from(changePoints).sort((a, b) => a - b);
    const points: PromillePoint[] = [];
    const pushPoint = (timestamp: number, value: number) => {
        const rounded = Number(Math.max(value, 0).toFixed(3));
        const last = points.at(-1);
        if (last && Math.abs(last.timestamp - timestamp) < 1) last.promille = rounded;
        else points.push({ timestamp, promille: rounded });
    };

    let currentPromille = events.instant.get(startMs) ?? 0;
    let currentRate = events.rate.get(startMs) ?? 0;
    let lastTimestamp = startMs;
    pushPoint(startMs, currentPromille);
    for (let index = 1; index < sortedPoints.length; index += 1) {
        const point = sortedPoints[index];
        const delta = point - lastTimestamp;
        if (delta > 0) {
            currentPromille = advancePromille(currentPromille, currentRate, eliminationPerMs, delta, lastTimestamp, pushPoint);
            lastTimestamp = point;
        }
        currentPromille += events.instant.get(point) ?? 0;
        currentRate += events.rate.get(point) ?? 0;
        pushPoint(point, currentPromille);
    }

    let zeroTimestamp = lastTimestamp;
    if (currentPromille > 0 && eliminationPerMs > 0) {
        zeroTimestamp += currentPromille / eliminationPerMs;
        pushPoint(zeroTimestamp, 0);
    }
    const finalEnd = Math.max(endMs, zeroTimestamp + TIMELINE_PADDING_MS);
    if (finalEnd > zeroTimestamp) pushPoint(finalEnd, 0);
    return points.sort((a, b) => a.timestamp - b.timestamp);
};

const advancePromille = (
    promille: number,
    currentRate: number,
    eliminationPerMs: number,
    delta: number,
    lastTimestamp: number,
    pushPoint: (timestamp: number, value: number) => void
): number => {
    const netRate = currentRate - eliminationPerMs;
    if (promille <= 0) return netRate > 0 ? promille + netRate * delta : promille;
    if (netRate >= 0) return promille + netRate * delta;
    const timeToZero = promille / -netRate;
    if (timeToZero < delta) {
        pushPoint(lastTimestamp + timeToZero, 0);
        return 0;
    }
    return promille + netRate * delta;
};

export const calculateMaxPromilleInRange = (
    points: PromillePoint[],
    startMs: number,
    endMs: number
): number => {
    if (points.length === 0 || endMs <= startMs) {
        return 0;
    }

    let max = getPromilleAtTime(points, startMs);

    points.forEach((point) => {
        if (point.timestamp < startMs || point.timestamp > endMs) {
            return;
        }
        max = Math.max(max, point.promille);
    });

    max = Math.max(max, getPromilleAtTime(points, endMs));
    return Number(max.toFixed(3));
};

export const deriveSoberInfo = (points: PromillePoint[], nowMs: number): SoberInfo => {
    if (points.length === 0) return {actual: null, projected: null};

    let lastPositiveIndex = -1;
    points.forEach((point, index) => {
        if (point.promille > 0) lastPositiveIndex = index;
    });

    if (lastPositiveIndex === -1) {
        return {actual: points.find((point) => point.promille <= 0)?.timestamp ?? null, projected: null};
    }

    for (let index = lastPositiveIndex + 1; index < points.length; index += 1) {
        const point = points[index];
        if (point.promille <= 0) {
            return {
                actual: point.timestamp <= nowMs ? point.timestamp : null,
                projected: point.timestamp > nowMs ? point.timestamp : null,
            };
        }
    }

    return {actual: null, projected: points.at(-1)!.timestamp};
};
